import React, { memo, useCallback, useMemo } from "react";

import TagMeasurement from "./TagMeasurement";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { Box, Grid, Stack, TextField, Typography, alpha, useTheme } from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { MeasurementsDaily } from "@/model/Application/Settings/SettingsResponseTypes";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface Props {
	measurementsByTag: MeasurementsDaily[];
	tagId: string;
	groupName: string;
	handleFieldChange: (
		id: number,
		labAnalyzeId: number,
		field: keyof MeasurementsDaily,
		value: MeasurementsDaily[keyof MeasurementsDaily],
	) => void;
	handleGroupDateChange: (
		groupName: string,
		tagId: string,
		field: keyof MeasurementsDaily,
		value: MeasurementsDaily[keyof MeasurementsDaily],
	) => void;
	handleDialogOpen: (labMeasurementId: number, labAnalyzeId: number) => void;
	sortBy?: string;
}

/**
 * TagRow component displays a row of tag information and associated measurements.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Array} props.measurementsByTag - Array of measurement objects associated with the tag.
 * @param {number} props.tagId - The ID of the tag.
 * @param {string} props.groupName - The name of the group to which the tag belongs.
 * @param {function} props.handleFieldChange - Function to handle changes in measurement fields.
 * @param {function} props.handleGroupDateChange - Function to handle changes in the group date.
 * @param {function} props.handleDialogOpen - Function to handle opening of a dialog.
 *
 * @returns {JSX.Element} The rendered TagRow component.
 *
 * @example
 * <TagRow
 *   measurementsByTag={measurementsByTag}
 *   tagId={123}
 *   groupName="GroupName"
 *   handleFieldChange={handleFieldChange}
 *   handleGroupDateChange={handleGroupDateChange}
 *   handleDialogOpen={handleDialogOpen}
 * />
 *
 * @remarks
 * - The component uses the `useTime` hook to get the current timezone.
 * - The `TextField` for the tag test date is disabled if all measurements have a `MeasurementType` of 1.
 * - The `handleGroupDateChange` function is called with the updated date in ISO format when the date is changed.
 * - The component maps over the `measurementsByTag` array to render `TagMeasurement` components for each measurement.
 */
const TagRow: React.FC<Props> = ({
	measurementsByTag,
	tagId,
	groupName,
	handleFieldChange,
	handleGroupDateChange,
	handleDialogOpen,
	sortBy,
}) => {
	const { timezone } = useTime();
	const theme = useTheme();

	// Memoize tag description to avoid recalculation
	const tagDescription = useMemo(() => {
		return measurementsByTag?.[0]?.TagDescription || "";
	}, [measurementsByTag]);

	// Memoize display text
	const displayText = useMemo(() => {
		if (sortBy === "name") {
			return `Tag Name: ${tagDescription} - ${tagId}`;
		}
		return `Tag ID: ${tagId} - ${tagDescription}`;
	}, [sortBy, tagDescription, tagId]);

	// Memoize disabled state
	const isDisabled = useMemo(() => {
		return measurementsByTag.every((el) => el.MeasurementType === 1);
	}, [measurementsByTag]);

	// Tag test date is empty by default
	const tagTestDate = "";

	// Optimized date change handler
	const handleDateChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			try {
				const value = e.target.value;
				if (!value) return;

				const date = dayjs(value).tz(timezone, true);
				if (!date.isValid()) {
					console.warn("Invalid date value:", value);
					return;
				}

				handleGroupDateChange(
					groupName,
					tagId,
					"TestDate",
					date.utc().toISOString(),
				);
			} catch (error) {
				console.error("Error updating tag date:", error);
			}
		},
		[groupName, tagId, timezone, handleGroupDateChange],
	);

	return (
		<Box
			sx={{
				mb: 2,
				pb: 1.5,
				borderBottom: 1,
				borderColor: "divider",
			}}
		>
			<Grid
				container
				spacing={1.5}
				alignItems="flex-end"
				sx={{
					mb: 1.5,
					mt: 0,
					px: 1.5,
					py: 1.5,
					border: "1px solid",
					borderColor: alpha(theme.palette.divider, 0.9),
					borderRadius: 2,
					bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : "background.paper",
				}}
			>
				<Grid item xs={12} sm={7} md={7}>
					<Stack spacing={0.75}>
						<Typography
							variant="subtitle2"
							sx={{
								fontWeight: 800,
								color: "text.primary",
								lineHeight: 1.2,
							}}
						>
							{displayText}
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={12} sm={4} md={4}>
					<TextField
						disabled={isDisabled}
						label={
							<Translate String="LabDaily.Group.TagTestDate">
								Tag Test Date
							</Translate>
						}
						type="datetime-local"
						size="small"
						value={tagTestDate}
						onChange={handleDateChange}
						InputLabelProps={{ shrink: true }}
						fullWidth
						sx={{
							minWidth: 0,
							"& .MuiOutlinedInput-root": {
								borderRadius: 2.5,
								bgcolor: "background.paper",
							},
							"& .MuiInputBase-input": {
								padding: "8px 10px",
							},
						}}
					/>
				</Grid>
			</Grid>

			<Box sx={{ mt: 0.5 }}>
				{measurementsByTag.map((measurement, index) => (
					<TagMeasurement
						handleDialogOpen={handleDialogOpen}
						key={index + measurement.LabMeasurementId}
						measurement={measurement}
						handleFieldChange={handleFieldChange}
					/>
				))}
			</Box>
		</Box>
	);
};

const areMeasurementsEqual = (
	prevMeasurements: MeasurementsDaily[],
	nextMeasurements: MeasurementsDaily[],
) =>
	prevMeasurements.length === nextMeasurements.length &&
	prevMeasurements.every(
		(measurement, index) => measurement === nextMeasurements[index],
	);

export default memo(
	TagRow,
	(prev, next) =>
		prev.tagId === next.tagId &&
		prev.groupName === next.groupName &&
		prev.sortBy === next.sortBy &&
		prev.handleFieldChange === next.handleFieldChange &&
		prev.handleGroupDateChange === next.handleGroupDateChange &&
		prev.handleDialogOpen === next.handleDialogOpen &&
		areMeasurementsEqual(prev.measurementsByTag, next.measurementsByTag),
);
